import { Injectable, signal } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { NotificationStateService } from './notification/notification-state.service';
import { FirebaseMessagingService, InAppNotification } from './firebase-messaging.service';
import { InboxService } from './notification/inbox.service';
import { NGXLogger } from 'ngx-logger';
import { Badge } from '@capawesome/capacitor-badge';
import { Capacitor } from '@capacitor/core';

@Injectable({ providedIn: 'root' })
export class NotificationBadgeService {
  private unreadCountSignal = signal<number>(0);
  public unreadCount = this.unreadCountSignal.asReadonly();
  public unreadCount$ = new BehaviorSubject<number>(0);

  public bellVisible = signal<boolean>(false);

  private recentNotificationIds = new Set<string>();
  private readonly RECENT_NOTIFICATIONS_TTL = 5 * 60 * 1000;
  private readonly STORAGE_KEY = 'unread_notifications_count';

  constructor(
    private notificationStateService: NotificationStateService,
    private firebaseMessaging: FirebaseMessagingService,
    private inboxService: InboxService,
    private logger: NGXLogger
  ) {
    this.initialize();
  }

  public initialize(): void {
    this.logger.info('🔔 NotificationBadgeService: Inicializando servicio...');
    this.initializeFromStorage();

    this.notificationStateService.state$.subscribe(state => {
      this.updateBadgeCount(state.unread.unreadCount);
    });

    this.firebaseMessaging.notification$.subscribe(notification => {
      if (notification) this.handlePushNotification(notification);
    });
  }

  public setBellVisible(isVisible: boolean): void {
    this.bellVisible.set(isVisible);
  }

  private handlePushNotification(notification: InAppNotification): void {
    this.logger.debug('📨 Badge: procesando push:', notification);

    const hard = (notification as any).unreadCount ?? (notification as any).unread_count;
    if (typeof hard === 'number') {
      this.logger.info('📊 Badge: set autoritativo (push):', hard);
      this.updateBadgeCount(hard);
      return;
    }

    const id = (notification as any).notificationId ?? (notification as any)?.data?.notification_id;
    if (id && !this.isRecentNotification(id)) {
      this.logger.info('➕ Badge: +1 optimista');
      this.incrementCountOptimistically();
      this.addRecentNotification(id);
    }
  }

  private isRecentNotification(notificationId: string): boolean {
    return this.recentNotificationIds.has(notificationId);
  }

  private addRecentNotification(notificationId: string): void {
    this.recentNotificationIds.add(notificationId);
    setTimeout(() => this.recentNotificationIds.delete(notificationId), this.RECENT_NOTIFICATIONS_TTL);
  }

  private incrementCountOptimistically(): void {
    this.updateBadgeCount(this.unreadCountSignal() + 1);
  }

  private updateBadgeCount(count: number): void {
    this.unreadCountSignal.set(count);
    this.unreadCount$.next(count);
    this.saveToStorage(count);
    
    this.updateSystemBadge(count);
  }

  private initializeFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      const count = stored ? parseInt(stored, 10) : NaN;
      if (!isNaN(count) && count >= 0) this.unreadCountSignal.set(count);
    } catch {}
  }

  private saveToStorage(count: number): void {
    try { localStorage.setItem(this.STORAGE_KEY, count.toString()); } catch {}
  }

  public loadUnreadCountFromServer(): void {
    this.inboxService.getUnreadCount().pipe(
      tap(res => {
        if (res?.success && typeof res.unread_count === 'number') {
          if (res.unread_count !== this.unreadCountSignal()) {
            this.updateBadgeCount(res.unread_count);
          }
        }
      }),
      catchError(err => {
        this.logger.error('UnreadCount server error:', err);
        return [];
      })
    ).subscribe();
  }

  public onInboxOpened(): void {
    this.loadUnreadCountFromServer();
  }

  public async decrementCount(): Promise<void> {
    try {
      if (Capacitor.isNativePlatform()) {
        const currentBadge = await Badge.get();
        const newCount = Math.max(0, currentBadge.count - 1);
        this.logger.debug(`Badge actual del sistema: ${currentBadge.count}, nuevo: ${newCount}`);
        this.updateBadgeCount(newCount);
      } else {
        this.updateBadgeCount(Math.max(0, this.unreadCountSignal() - 1));
      }
    } catch (error) {
      this.logger.error('Error obteniendo badge actual:', error);
      this.updateBadgeCount(Math.max(0, this.unreadCountSignal() - 1));
    }
  }

  public resetCount(): void {
    this.updateBadgeCount(0);
  }

  private async updateSystemBadge(count: number): Promise<void> {
    try {
      if (Capacitor.isNativePlatform()) {
        await Badge.set({ count });
        this.logger.debug(`Badge del sistema actualizado a: ${count}`);
      }
    } catch (error) {
      this.logger.error('Error actualizando badge del sistema:', error);
    }
  }

  public getCurrentCount(): number {
    return this.unreadCountSignal();
  }

  public clearStorage(): void {
    try { localStorage.removeItem(this.STORAGE_KEY); } catch {}
  }
}
